import React, { useContext, useEffect, useRef, useState } from 'react';
import { isNullOrUndefined } from '@utils/utils';
import { Dropdown } from '@components/RingilComponents/SafeDropdown';
import { CompanyModuleEnum, LovDTO, ModuleSettingsOutDTO } from '@api/ringil-api.v2';
import { AuthenticatedUserContext } from '@components/auth/AuthenticatedUser.tsx';
import { deferStampDataTestIdOnDescendantRef, toTestIdSlug } from '@utils/testUtils';

interface ComponentProps {
  companyId: string;
  onChange: (companyId: string, companyCode: string) => void;
  onChangeCompanyCode: (companyCode: string) => void;
  style?: any;
  moduleType: CompanyModuleEnum;
  placeholder?: string;
  readonly?: boolean;
  filterEnabled?: boolean;
  dataTestId?: string;
}

interface LovWithPreference extends Omit<LovDTO, 'active'> {
  preferred: boolean;
  companyCode?: string;
}

/**
 * Entity owner is the paying customer of the functionality or the inherited party that creates the entity for the pauint customer
 * @param partnerId
 * @param onChange
 * @param style
 * @constructor
 */
const InputEntityOwner: React.FC<ComponentProps> = ({
                                                      moduleType,
                                                      companyId,
                                                      onChange,
                                                      style,
                                                      placeholder,
                                                      onChangeCompanyCode,
                                                      readonly = false,
                                                      filterEnabled = false,
                                                      dataTestId,
                                                    }) => {
  const { loggedUser, isReady } = useContext(AuthenticatedUserContext);
  const [companies, setCompanies] = useState<LovWithPreference[]>();
  const wrapperRef = useRef<HTMLDivElement | null>(null);

  const convertToCompany = (t: ModuleSettingsOutDTO): LovWithPreference => {
    return {
      id: t.inheritingModule ? t.owner.companyId : loggedUser.companyId,
      name: t.inheritingModule ? t.owner.companyName : loggedUser.company,
      companyCode: loggedUser.companyCode ? loggedUser.companyCode : t.owner?.companyCode,
      preferred: !t.inheritingModule,
    };
  };

  useEffect(() => {
    if (isReady) {
      const c = loggedUser?.modules
        .filter(t => t.companyModule === moduleType)
        .reduce((previousValue, currentValue) => {
          return [...previousValue, convertToCompany(currentValue)];
        }, []);
      setCompanies(c);
    }
  }, [isReady]);

  useEffect(() => {
    if (companies && companies.length > 0 && isNullOrUndefined(companyId)) {
      onChange(companies.find(t => t.preferred)?.id || companies[0].id, companies.find(t => t.preferred)?.companyCode || companies[0].companyCode);
      if (onChangeCompanyCode) {
        onChangeCompanyCode(companies.find(t => t.preferred)?.companyCode || companies[0].companyCode);
      }
    }
  }, [companies]);

  useEffect(() => {
    return deferStampDataTestIdOnDescendantRef(wrapperRef, '.p-dropdown', dataTestId);
  }, [companies, dataTestId]);

  const truncateName = (name: string, maxLength: number = 25) => {
    if (name && name.length > maxLength) {
      return name.substring(0, maxLength) + '...';
    }
    return name;
  };

  const selectedCompanyTemplate = (option: LovWithPreference, props: any) => {
    if (option) {
      return (
        <div>{truncateName(option.name)}</div>
      );
    }

    return (
      <span>
        {props.placeholder}
      </span>
    );
  };

  const companyOptionTemplate = (option: LovWithPreference) => {
    if (option) {
      return (
        <div data-testid={dataTestId ? `${dataTestId}-${toTestIdSlug(option.name)}` : undefined}>{truncateName(option.name)}</div>
      );
    }
    return <span>{placeholder}</span>;
  };

  return (
    <>
      {loggedUser && (
        <div ref={wrapperRef} style={{ width: '100%' }}>
          <Dropdown
            style={{ width: '100%', ...style }}
            value={companyId}
            options={companies}
            optionLabel={'name'}
            optionValue={'id'}
            placeholder={placeholder}
            disabled={readonly}
            valueTemplate={selectedCompanyTemplate}
            itemTemplate={companyOptionTemplate}
            onChange={e => {
              onChange(e.value, companies.find(t => t.id == e.value)?.companyCode);
              if (onChangeCompanyCode) {
                onChangeCompanyCode(companies.find(t => t.id == e.value)?.companyCode);
              }
            }}
            filter={filterEnabled}
          />
        </div>
      )}
    </>
  );
};

export default InputEntityOwner;
